import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Change Governance — the Approval Center's data layer.
 *
 * Replaces the retired `useApprovals` hook, which paginated client-side over a
 * full `/api/approval/all` fetch and typed the response against fields the
 * backend never returned.
 */

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface PagedResponse<T> {
  data: T[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

/** Mirrors ApprovalRequestStatus on the server. Always upper-case. */
export type ApprovalRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'APPLIED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'EXPIRED'
  | 'CONFLICTED'
  | 'APPLY_FAILED';

export type ApprovalStageStatus =
  | 'WAITING'
  | 'ACTIVE'
  | 'APPROVED'
  | 'REJECTED'
  | 'SKIPPED'
  | 'EXPIRED';

export type ApprovalScope = 'inbox' | 'mine' | 'all';

export interface ApprovalRequestSummary {
  approvalRequestId: number;
  operationKind: string;
  targetType: string;
  targetId: string;
  targetLabel?: string | null;
  status: ApprovalRequestStatus;
  mode: 'STAGED' | 'ADVISORY';
  policyCode: string;
  currentStageIndex: number;
  requestedByUserId: number;
  requestedDate: string;
  dueDate?: string | null;
  requestComment?: string | null;
}

export interface ApprovalStage {
  approvalRequestStageId: number;
  stageIndex: number;
  stageCode: string;
  stageName: string;
  status: ApprovalStageStatus;
  quorum: string;
  quorumThreshold: number;
  activatedDate?: string | null;
  resolvedDate?: string | null;
  slaHours?: number | null;
  dueDate?: string | null;
  escalationLevel: number;
  approverSpecJson?: string | null;
}

export interface ApprovalDecision {
  approvalDecisionId: number;
  approvalRequestStageId?: number | null;
  decidedByUserId: number;
  /** Resolved approver name; optional so an older API degrades to the id. */
  decidedByUserName?: string | null;
  decision: 'APPROVE' | 'REJECT' | 'COMMENT' | 'DELEGATE';
  comment?: string | null;
  decidedDate: string;
}

export interface ApprovalRequestDetail extends ApprovalRequestSummary {
  policyVersion: number;
  resolvedDate?: string | null;
  resolutionComment?: string | null;
  appliedDate?: string | null;
  applyError?: string | null;
  correlationId: string;
  diffJson?: string | null;
  proposedJson?: string | null;
  baselinePredicateJson?: string | null;
  canDecide: boolean;
  canWithdraw: boolean;
  stages: ApprovalStage[];
  decisions: ApprovalDecision[];
}

/** One row of the reviewer's side-by-side comparison. */
export interface ApprovalFieldDiff {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

/** The server stores the diff as JSON; unpack it for rendering. */
export function parseDiff(diffJson?: string | null): ApprovalFieldDiff[] {
  if (!diffJson) return [];
  try {
    const parsed = JSON.parse(diffJson) as Record<string, { old?: string | null; new?: string | null }>;
    return Object.entries(parsed).map(([field, value]) => ({
      field,
      oldValue: value?.old ?? null,
      newValue: value?.new ?? null,
    }));
  } catch {
    // A malformed diff must not blank the whole detail page — the stages and
    // decisions are still worth showing.
    return [];
  }
}

export const approvalKeys = {
  all: ['approvalRequests'] as const,
  list: (scope: ApprovalScope, page: number, status?: string) =>
    [...approvalKeys.all, 'list', scope, page, status ?? ''] as const,
  detail: (id: number) => [...approvalKeys.all, 'detail', id] as const,
  inboxCount: [...['approvalRequests'], 'inboxCount'] as const,
  pendingFor: (targetType: string, targetId: string) =>
    [...approvalKeys.all, 'pendingFor', targetType, targetId] as const,
};

export function useApprovalRequests(params: {
  scope?: ApprovalScope;
  status?: string;
  operationKind?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  const { scope = 'inbox', status, operationKind, page = 1, pageSize = 20, enabled = true } = params;

  return useQuery({
    queryKey: approvalKeys.list(scope, page, status),
    enabled,
    queryFn: async () => {
      const { data } = await api.get<PagedResponse<ApprovalRequestSummary>>(
        '/api/v2/approvals/requests',
        { params: { scope, status, operationKind, page, pageSize } }
      );
      return data;
    },
  });
}

export function useApprovalRequest(id: number | undefined) {
  return useQuery({
    queryKey: approvalKeys.detail(id ?? 0),
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<ApprovalRequestDetail>>(
        `/api/v2/approvals/requests/${id}`
      );
      return data.data;
    },
  });
}

/** Badge count for the sidebar. */
export function useApprovalInboxCount(enabled = true) {
  return useQuery({
    queryKey: approvalKeys.inboxCount,
    enabled,
    // Phase 1 has no push channel, so the inbox is polled. Kept slow enough to
    // be invisible in the network tab and fast enough to feel live.
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<{ count: number }>>('/api/v2/approvals/inbox/count');
      return data.data.count;
    },
  });
}

/** Does this record have a change staged against it? */
export function usePendingApproval(targetType: string, targetId?: string, enabled = true) {
  return useQuery({
    queryKey: approvalKeys.pendingFor(targetType, targetId ?? ''),
    enabled: enabled && !!targetId,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<{
        approvalRequestId: number;
        status: ApprovalRequestStatus;
        operationKind: string;
        requestedDate: string;
      } | null>>('/api/v2/approvals/requests/pending-for', {
        params: { targetType, targetId },
      });
      return data.data;
    },
  });
}

type DecisionAction = 'approve' | 'reject' | 'withdraw' | 'comment';

export function useApprovalDecision(action: DecisionAction) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, comment }: { id: number; comment?: string }) => {
      const { data } = await api.post<ApiResponse<{ outcome: string }>>(
        `/api/v2/approvals/requests/${id}/${action}`,
        { comment }
      );
      return data.data;
    },
    onSuccess: (_result, variables) => {
      // A decision can advance a stage, resolve the request, and change the
      // badge count all at once, so invalidate the whole namespace rather than
      // trying to predict which views moved.
      queryClient.invalidateQueries({ queryKey: approvalKeys.all });
      queryClient.invalidateQueries({ queryKey: approvalKeys.detail(variables.id) });
    },
  });
}
